/**
 * Pure derivations from (artists, preferences) for schedule and conflict views.
 * These replace the former server-side getSchedule/getConflicts queries.
 */
import type { ArtistDTO, StageDTO } from "./api";
import type { EventPrefs, PreferenceMap, Rating, SetPreferenceMap } from "./preferences";
import { fmtTime } from "./format";

export type ScheduleItem = {
  artist: ArtistDTO;
  rating: Rating;
  set: ArtistDTO["sets"][number];
};

/**
 * Resolve the effective rating for a specific set: a per-set override wins
 * over the artist-level default. Null means unrated.
 */
export function effectiveRating(
  artistSlug: string,
  setId: number,
  prefs: PreferenceMap,
  setPrefs: SetPreferenceMap = {},
): Rating | null {
  return setPrefs[setId] ?? prefs[artistSlug] ?? null;
}

export type ScheduleLane = {
  stage: StageDTO;
  sets: ScheduleItem[];
};

export type ScheduleDay = {
  day: number;
  lanes: ScheduleLane[];
};

export type ConflictEntry = {
  day: number;
  overlapStart: number;
  overlapEnd: number;
  startLabel: string;
  endLabel: string;
  artists: Array<{
    slug: string;
    name: string;
    stage: string;
    rating: Rating;
  }>;
};

/**
 * Build a schedule from the user's preferences. Only "like" and "superlike"
 * appear by default — callers can override. Takes both the artist-level map
 * and an optional per-set override map; the per-set rating wins when set.
 */
export function deriveSchedule(
  artists: ArtistDTO[],
  prefs: PreferenceMap,
  stages: StageDTO[],
  opts: { ratings?: Rating[]; setPrefs?: SetPreferenceMap; days?: number } = {},
): ScheduleDay[] {
  const wanted = new Set<Rating>(opts.ratings ?? ["like", "superlike"]);
  const setPrefs = opts.setPrefs ?? {};
  // Number of festival days: caller override, else the max day seen across
  // all sets (handles LIB's 5, Creamfields' 4, etc.), else default to 3.
  const maxDayInSets = artists.reduce(
    (m, a) => a.sets.reduce((mm, s) => Math.max(mm, s.day), m),
    0,
  );
  const dayCount = opts.days ?? (maxDayInSets > 0 ? maxDayInSets : 3);
  const days: ScheduleDay[] = [];
  for (let day = 1; day <= dayCount; day++) {
    const lanes: ScheduleLane[] = [];
    for (const stage of stages) {
      const sets: ScheduleItem[] = [];
      for (const a of artists) {
        for (const set of a.sets) {
          if (set.day !== day || set.stage.slug !== stage.slug) continue;
          const rating = effectiveRating(a.slug, set.id, prefs, setPrefs);
          if (!rating || !wanted.has(rating)) continue;
          sets.push({ artist: a, rating, set });
        }
      }
      if (sets.length) {
        sets.sort((a, b) => a.set.start - b.set.start);
        lanes.push({ stage, sets });
      }
    }
    days.push({ day, lanes });
  }
  return days;
}

/** Detect overlapping picks on different stages across the same day. */
export function deriveConflicts(
  artists: ArtistDTO[],
  prefs: PreferenceMap,
  opts: { setPrefs?: SetPreferenceMap } = {},
): ConflictEntry[] {
  const setPrefs = opts.setPrefs ?? {};
  type Liked = {
    slug: string; name: string; stage: string;
    rating: Rating; day: number; start: number; end: number;
  };
  const liked: Liked[] = [];
  for (const a of artists) {
    for (const s of a.sets) {
      const rating = effectiveRating(a.slug, s.id, prefs, setPrefs);
      if (rating !== "like" && rating !== "superlike") continue;
      liked.push({
        slug: a.slug,
        name: a.name,
        stage: s.stage.name,
        rating,
        day: s.day,
        start: s.start,
        end: s.end,
      });
    }
  }
  liked.sort((a, b) => a.day - b.day || a.start - b.start);

  const conflicts: ConflictEntry[] = [];
  for (let i = 0; i < liked.length; i++) {
    const a = liked[i]!;
    for (let j = i + 1; j < liked.length; j++) {
      const b = liked[j]!;
      if (b.day !== a.day) break;
      if (b.start >= a.end) break;
      if (a.stage === b.stage) continue;
      const overlapStart = Math.max(a.start, b.start);
      const overlapEnd = Math.min(a.end, b.end);
      if (overlapEnd <= overlapStart) continue;
      const existing = conflicts.find(
        c => c.day === a.day && c.overlapStart === overlapStart && c.overlapEnd === overlapEnd,
      );
      if (existing) {
        const names = new Set(existing.artists.map(x => x.slug));
        if (!names.has(a.slug)) existing.artists.push({ slug: a.slug, name: a.name, stage: a.stage, rating: a.rating });
        if (!names.has(b.slug)) existing.artists.push({ slug: b.slug, name: b.name, stage: b.stage, rating: b.rating });
      } else {
        conflicts.push({
          day: a.day,
          overlapStart,
          overlapEnd,
          startLabel: fmtTime(overlapStart),
          endLabel: fmtTime(overlapEnd),
          artists: [
            { slug: a.slug, name: a.name, stage: a.stage, rating: a.rating },
            { slug: b.slug, name: b.name, stage: b.stage, rating: b.rating },
          ],
        });
      }
    }
  }
  return conflicts;
}
